// ═══════════════════════════════════════════════════════════════════════════════
// BriefingsListPage — catalog of named briefings (SME-friendly)
// ═══════════════════════════════════════════════════════════════════════════════
//
// PURPOSE
//   Lists every briefing the merchant can use. Default view is cards grouped by
//   status (Published → Drafts → Inactive); engineer-friendly signals (raw Code,
//   PublishedAt, etc.) sit behind an "Advanced" view toggle.
//
// LIFECYCLE SHAPE
//   - Create always produces a DRAFT (invisible to runtime).
//   - Publish + Edit metadata + Kill switch live on the detail page.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AppstoreOutlined,
  CloudUploadOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ReadOutlined,
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { colors, typography } from '@/theme/tokens';
import {
  createBriefingDraft,
  listBriefings,
  previewBriefing,
  searchMembersForPreview,
  type BriefingContext,
} from '@/api/briefingContext';
import { BriefingPreviewDrawer } from '@shared/briefing-preview';
import { sessionHelper } from '@/auth/sessionHelper';

const { Text, Title, Paragraph } = Typography;

type ViewMode = 'cards' | 'table';
const VIEW_MODE_KEY = 'agentstudio.briefings.viewMode';

// Plain-language status meta. Drives card chrome (color strip, accent tag).
interface StatusMeta {
  label: string;
  tone: 'green' | 'amber' | 'slate';
  accent: string;
  blurb: string;
}

function getStatusMeta(b: BriefingContext): StatusMeta {
  if (!b.isActive) {
    return {
      label: 'Inactive',
      tone: 'slate',
      accent: colors.slate[400],
      blurb: 'Disabled via the kill switch — runtime skips this briefing.',
    };
  }
  if (b.isDraft) {
    return {
      label: 'Draft',
      tone: 'amber',
      accent: colors.amber[500],
      blurb: 'Not yet published. Invisible to the runtime until you Publish from the detail page.',
    };
  }
  return {
    label: 'Published',
    tone: 'green',
    accent: colors.green[600],
    blurb: 'Live. Linked agents receive this briefing on every turn.',
  };
}

export function BriefingsListPage() {
  const [rows, setRows] = useState<BriefingContext[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [includeDrafts, setIncludeDrafts] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [previewRow, setPreviewRow] = useState<BriefingContext | null>(null);
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const v = window.localStorage.getItem(VIEW_MODE_KEY);
      return v === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });

  function setViewModePersisted(v: ViewMode): void {
    setViewMode(v);
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, v);
    } catch {
      /* ignore */
    }
  }

  async function load() {
    setLoading(true);
    try {
      const data = await listBriefings({ includeInactive, includeDrafts });
      setRows(data);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to load briefings');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive, includeDrafts]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.code, r.name, r.description].filter(Boolean).some((s) => s!.toLowerCase().includes(q)),
    );
  }, [rows, search]);

  // Group cards by status for the SME view. Published first (most relevant),
  // then Drafts (work in progress), then Inactive (kill-switched). Within each
  // group, order by Code so the layout is stable across reloads.
  const grouped = useMemo(() => {
    if (viewMode !== 'cards') return [] as Array<[StatusMeta, BriefingContext[]]>;
    const published: BriefingContext[] = [];
    const drafts: BriefingContext[] = [];
    const inactive: BriefingContext[] = [];
    for (const r of visible) {
      if (!r.isActive) inactive.push(r);
      else if (r.isDraft) drafts.push(r);
      else published.push(r);
    }
    const cmp = (a: BriefingContext, b: BriefingContext) => a.code.localeCompare(b.code);
    published.sort(cmp);
    drafts.sort(cmp);
    inactive.sort(cmp);
    const result: Array<[StatusMeta, BriefingContext[]]> = [];
    if (published.length)
      result.push([
        { label: 'Published', tone: 'green', accent: colors.green[600], blurb: '' },
        published,
      ]);
    if (drafts.length)
      result.push([
        { label: 'Drafts', tone: 'amber', accent: colors.amber[500], blurb: '' },
        drafts,
      ]);
    if (inactive.length)
      result.push([
        { label: 'Inactive', tone: 'slate', accent: colors.slate[400], blurb: '' },
        inactive,
      ]);
    return result;
  }, [visible, viewMode]);

  const stats = useMemo(() => {
    return {
      total: visible.length,
      drafts: visible.filter((r) => r.isDraft && r.isActive).length,
      published: visible.filter((r) => !r.isDraft && r.isActive).length,
      inactive: visible.filter((r) => !r.isActive).length,
    };
  }, [visible]);

  async function handleCreate(payload: { code: string; name?: string; description?: string }) {
    try {
      const saved = await createBriefingDraft(payload);
      message.success(`Created draft '${saved.code}'`);
      setCreateOpen(false);
      navigate(`/briefings/${saved.id}`);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Create failed');
    }
  }

  // ── Advanced table columns ───────────────────────────────────────────────
  const columns: ColumnsType<BriefingContext> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: (code: string, row) => (
        <Space direction="vertical" size={0}>
          <Link to={`/briefings/${row.id}`}>
            <Text style={{ fontFamily: typography.fontMono, fontWeight: 600, fontSize: 12.5 }}>
              {code}
            </Text>
          </Link>
          {row.name && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.name}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text?: string) => (
        <Text type="secondary" style={{ fontSize: 12.5 }}>
          {text || '—'}
        </Text>
      ),
    },
    {
      title: 'Scope',
      dataIndex: 'merchantId',
      key: 'scope',
      width: 110,
      render: (mid?: number | null) =>
        mid == null ? (
          <Tooltip title="Visible to every merchant unless overridden privately.">
            <Tag color="blue" style={{ marginRight: 0 }}>
              Global
            </Tag>
          </Tooltip>
        ) : (
          <Tooltip title={`Private to merchant ${mid}. Wins over global Code clashes.`}>
            <Tag color="geekblue" style={{ marginRight: 0 }}>
              M{mid}
            </Tag>
          </Tooltip>
        ),
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 90,
      render: (v: number) => (
        <Text style={{ fontFamily: typography.fontMono, fontSize: 12 }}>v{v}</Text>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_v, row) => {
        const meta = getStatusMeta(row);
        return <StatusPill tone={meta.tone}>{meta.label}</StatusPill>;
      },
    },
    {
      title: 'Published',
      dataIndex: 'publishedAtUtc',
      key: 'publishedAtUtc',
      width: 150,
      render: (v?: string | null) =>
        v ? (
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            {new Date(v).toLocaleDateString()}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      align: 'right',
      render: (_v, row) => (
        <Link to={`/briefings/${row.id}`}>
          <Button type="text" size="small" icon={<RightOutlined />} />
        </Link>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<ReadOutlined />}
        title={
          <Space size={6} align="center">
            <span>Briefings</span>
            <HelpHotspot topic="briefing-context.briefings" />
          </Space>
        }
        subtitle="Named bundles of patient-state elements. Agents link briefings to receive context every turn."
        count={rows.length}
        countLabel={rows.length === 1 ? 'briefing' : 'briefings'}
      />

      {/* ── Filter bar ──────────────────────────────────────────────── */}
      <SectionCard noPadding>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
          }}
        >
          <Input.Search
            placeholder="Search by name, code, or description…"
            allowClear
            style={{ width: 320 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Tooltip title="Show drafts (work-in-progress — invisible to runtime).">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <Switch size="small" checked={includeDrafts} onChange={setIncludeDrafts} />
              Drafts
            </span>
          </Tooltip>
          <Tooltip title="Show inactive (kill-switched) briefings.">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <Switch size="small" checked={includeInactive} onChange={setIncludeInactive} />
              Inactive
            </span>
          </Tooltip>

          <span style={{ flex: 1 }} />

          <Space size={6}>
            <Tag color="default">{stats.total} shown</Tag>
            {stats.published > 0 && <Tag color="green">{stats.published} live</Tag>}
            {stats.drafts > 0 && <Tag color="orange">{stats.drafts} draft</Tag>}
            {stats.inactive > 0 && <Tag color="default">{stats.inactive} inactive</Tag>}
          </Space>

          <Segmented<ViewMode>
            size="middle"
            value={viewMode}
            onChange={(v) => setViewModePersisted(v as ViewMode)}
            options={[
              { value: 'cards', label: 'Cards', icon: <AppstoreOutlined /> },
              {
                value: 'table',
                label: (
                  <Tooltip title="Advanced view — table with code, scope, version, published timestamps">
                    <span>Advanced</span>
                  </Tooltip>
                ),
                icon: <InfoCircleOutlined />,
              },
            ]}
          />

          <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New briefing
          </Button>
        </div>
      </SectionCard>

      {/* ── Body ────────────────────────────────────────────────────── */}
      {viewMode === 'cards' ? (
        <CardsView
          grouped={grouped}
          loading={loading}
          hasSearch={!!search.trim()}
          onOpen={(b) => navigate(`/briefings/${b.id}`)}
          onPreview={(b) => setPreviewRow(b)}
        />
      ) : (
        <SectionCard noPadding>
          <Table<BriefingContext>
            rowKey="id"
            columns={columns}
            dataSource={visible}
            loading={loading}
            size="middle"
            pagination={false}
            rowClassName={(row) => (row.isActive ? '' : 'briefing-row-inactive')}
            onRow={(row) => ({
              onClick: () => navigate(`/briefings/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: (
                <BrandedEmpty
                  icon={<ReadOutlined />}
                  title="No briefings"
                  description="Create a new briefing to start composing patient context."
                />
              ),
            }}
          />
        </SectionCard>
      )}

      <style>{`
        .briefing-row-inactive { opacity: 0.55; }
        .briefing-row-inactive:hover { opacity: 0.85; }
      `}</style>

      <CreateBriefingDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />

      {previewRow && (
        <BriefingPreviewDrawer
          open={!!previewRow}
          onClose={() => setPreviewRow(null)}
          briefing={{
            id: previewRow.id,
            code: previewRow.code,
            name: previewRow.name,
            version: previewRow.version,
            isDraft: previewRow.isDraft,
            isActive: previewRow.isActive,
          }}
          loadPreview={previewBriefing}
          searchPatients={(q) => {
            const merchantId = sessionHelper.getSelectedMerchantId();
            if (!merchantId) {
              return Promise.reject(new Error('Pick a merchant from the top bar before previewing.'));
            }
            return searchMembersForPreview(merchantId, q);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CardsView — SME default. Cards grouped by status.
// ─────────────────────────────────────────────────────────────────────────────

interface CardsViewProps {
  grouped: Array<[StatusMeta, BriefingContext[]]>;
  loading: boolean;
  hasSearch: boolean;
  onOpen: (b: BriefingContext) => void;
  onPreview: (b: BriefingContext) => void;
}

function CardsView({ grouped, loading, hasSearch, onOpen, onPreview }: CardsViewProps) {
  if (loading && grouped.length === 0) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 12,
        }}
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            style={{
              height: 180,
              borderRadius: 12,
              background: colors.slate[100],
              border: `1px solid ${colors.slate[200]}`,
              opacity: 0.6,
            }}
          />
        ))}
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <SectionCard>
        <div style={{ padding: 32 }}>
          <BrandedEmpty
            icon={<ReadOutlined />}
            title={hasSearch ? 'No briefings match' : 'No briefings'}
            description={
              hasSearch
                ? 'Try a different search term, or clear the search.'
                : 'Create a new briefing to start composing patient context.'
            }
          />
        </div>
      </SectionCard>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {grouped.map(([meta, items]) => (
        <div key={meta.label}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 10,
              padding: '0 4px',
            }}
          >
            <Title level={5} style={{ margin: 0, fontSize: 14 }}>
              {meta.label}
            </Title>
            <StatusPill tone={meta.tone}>{items.length}</StatusPill>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 12,
            }}
          >
            {items.map((b) => (
              <BriefingCard
                key={b.id}
                briefing={b}
                onOpen={() => onOpen(b)}
                onPreview={() => onPreview(b)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BriefingCard — single briefing rendered for an SME.
// ─────────────────────────────────────────────────────────────────────────────

interface BriefingCardProps {
  briefing: BriefingContext;
  onOpen: () => void;
  onPreview: () => void;
}

function BriefingCard({ briefing, onOpen, onPreview }: BriefingCardProps) {
  const [hover, setHover] = useState(false);
  const status = getStatusMeta(briefing);
  const isInactive = !briefing.isActive;
  const publishedDate = briefing.publishedAtUtc
    ? new Date(briefing.publishedAtUtc).toLocaleDateString()
    : null;

  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        cursor: 'pointer',
        background: '#FFFFFF',
        border: `1px solid ${colors.slate[200]}`,
        borderLeft: `4px solid ${status.accent}`,
        borderRadius: 12,
        padding: '14px 16px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        opacity: isInactive ? 0.6 : 1,
        boxShadow: hover ? `0 6px 16px ${colors.slate[200]}66` : '0 1px 2px transparent',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'box-shadow 0.15s, transform 0.15s, border-color 0.15s',
      }}
    >
      {/* Header — name + scope chip */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            strong
            style={{ fontSize: 15, color: colors.slate[900], lineHeight: 1.3, display: 'block' }}
          >
            {briefing.name || briefing.code}
          </Text>
          <Text
            type="secondary"
            style={{
              fontFamily: typography.fontMono,
              fontSize: 11.5,
              display: 'block',
              marginTop: 2,
            }}
          >
            {briefing.code}
          </Text>
        </div>
        {briefing.merchantId == null ? (
          <Tooltip title="Visible to every merchant.">
            <Tag color="blue" style={{ marginRight: 0, fontSize: 10.5 }}>
              Global
            </Tag>
          </Tooltip>
        ) : (
          <Tooltip title={`Private to merchant ${briefing.merchantId}. Wins over global Code clashes.`}>
            <Tag color="geekblue" style={{ marginRight: 0, fontSize: 10.5 }}>
              M{briefing.merchantId}
            </Tag>
          </Tooltip>
        )}
        <Tooltip title="Live preview against a patient (debug)">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onPreview();
            }}
            style={{ opacity: hover ? 1 : 0.45, transition: 'opacity 0.15s' }}
          />
        </Tooltip>
      </div>

      {/* Description */}
      {briefing.description && (
        <Paragraph
          type="secondary"
          ellipsis={{ rows: 2 }}
          style={{ fontSize: 12.5, marginBottom: 0, lineHeight: 1.5 }}
        >
          {briefing.description}
        </Paragraph>
      )}

      {/* Footer — status + version + published date */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 'auto',
          paddingTop: 4,
        }}
      >
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        <Text
          type="secondary"
          style={{ fontFamily: typography.fontMono, fontSize: 11.5 }}
        >
          v{briefing.version}
        </Text>
        {publishedDate && (
          <Tooltip title="Published at">
            <Space size={4} style={{ marginLeft: 'auto' }}>
              <CloudUploadOutlined style={{ fontSize: 11, color: colors.slate[400] }} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {publishedDate}
              </Text>
            </Space>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CreateBriefingDrawer — minimal "Code + Name + Description" drawer
// ─────────────────────────────────────────────────────────────────────────────

interface CreateProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: { code: string; name?: string; description?: string }) => void;
}

function CreateBriefingDrawer({ open, onClose, onCreate }: CreateProps) {
  const [form] = Form.useForm<{ code: string; name?: string; description?: string }>();

  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  function handleOk() {
    form
      .validateFields()
      .then((vals) => onCreate(vals))
      .catch(() => {
        /* validation messages render inline */
      });
  }

  return (
    <Drawer
      title="New briefing"
      width={480}
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleOk}>
            Create draft
          </Button>
        </Space>
      }
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
        A briefing starts as a draft — invisible to the runtime. Add elements from the detail page,
        then click Publish.
      </Text>

      <Form layout="vertical" form={form} autoComplete="off">
        <Form.Item name="name" label="Name" rules={[{ max: 200 }]}>
          <Input placeholder="FOLFOX patient briefing" />
        </Form.Item>

        <Form.Item name="description" label="Description" rules={[{ max: 1000 }]}>
          <Input.TextArea
            rows={3}
            placeholder="What this briefing provides to the agent…"
          />
        </Form.Item>

        <Form.Item
          name="code"
          label="Code (advanced)"
          tooltip="Stable engineering identifier. Lowercase, hyphenated (e.g. folfox-patient-context). Immutable post-create."
          rules={[
            { required: true, message: 'Code is required' },
            {
              pattern: /^[a-z0-9-]+$/,
              message: 'Lowercase letters, digits, and dashes only',
            },
            { max: 80 },
          ]}
        >
          <Input
            placeholder="folfox-patient-context"
            style={{ fontFamily: typography.fontMono }}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
